import React, { useState, useEffect } from 'react';
import { getUserStats, getMyTransactions } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Wallet,
  Smartphone,
  TrendingUp,
  Activity,
  Percent,
  PiggyBank,
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, subtitle, color = 'primary' }) => (
  <Card className="bg-card border-border/40 hover:border-primary/30 transition-colors">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
            {title}
          </p>
          <p className={`text-4xl font-black tracking-tighter mt-2 metric-value`} style={{ color: `hsl(var(--${color}))` }}>
            {value}
          </p>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg`} style={{ backgroundColor: `hsl(var(--${color}) / 0.1)` }}>
          <Icon className="w-6 h-6" style={{ color: `hsl(var(--${color}))` }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function UserDashboard() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await refreshUser();
        const [statsRes, txRes] = await Promise.all([
          getUserStats(),
          getMyTransactions(),
        ]);
        setStats(statsRes.data);
        setTransactions(txRes.data.slice(0, 5));
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="user-dashboard">
      <div>
        <h1 className="text-4xl font-black tracking-tight">
          Hola, {user?.username}!
        </h1>
        <p className="text-muted-foreground mt-1">
          Bienvenido a tu panel de recargas
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Saldo Disponible"
          value={formatCurrency(stats?.balance || 0)}
          icon={Wallet}
          color="accent"
        />
        <StatCard
          title="Mi Descuento"
          value={`${stats?.discount_percentage || 0}%`}
          icon={Percent}
          color="primary"
        />
        <StatCard
          title="Total Recargas"
          value={stats?.total_transactions || 0}
          icon={Activity}
          color="primary"
        />
        <StatCard
          title="Total Ahorrado"
          value={formatCurrency(stats?.total_saved || 0)}
          subtitle="Gracias a tu descuento"
          icon={PiggyBank}
          color="accent"
        />
      </div>

      {/* Quick Info */}
      <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-border/40">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-lg bg-primary/20">
                <TrendingUp className="w-8 h-8 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">
                  Total Gastado
                </p>
                <p className="text-3xl font-black tracking-tighter text-primary metric-value">
                  {formatCurrency(stats?.total_spent || 0)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-muted-foreground">
                Con tu descuento del <span className="text-primary font-bold">{stats?.discount_percentage || 0}%</span>
              </p>
              <p className="text-sm text-muted-foreground">
                has ahorrado <span className="text-accent font-bold">{formatCurrency(stats?.total_saved || 0)}</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Transactions */}
      <Card className="bg-card border-border/40">
        <CardHeader>
          <CardTitle className="text-xl font-bold flex items-center gap-2">
            <Activity className="w-5 h-5 text-accent" />
            Mis Últimas Recargas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Smartphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aún no has realizado recargas</p>
              <p className="text-sm">¡Haz tu primera recarga ahora!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead>Servicio</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Monto</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow
                    key={tx.id}
                    className="border-border/30 hover:bg-secondary/30"
                  >
                    <TableCell className="font-medium">{tx.service_name}</TableCell>
                    <TableCell className="font-mono">{tx.phone_number}</TableCell>
                    <TableCell className="font-mono text-accent font-bold">
                      {formatCurrency(tx.final_price)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          tx.status === 'completed'
                            ? 'status-active'
                            : tx.status === 'pending'
                            ? 'status-pending'
                            : 'status-inactive'
                        }
                      >
                        {tx.status === 'completed'
                          ? 'Completado'
                          : tx.status === 'pending'
                          ? 'Pendiente'
                          : 'Fallido'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(tx.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
